import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    // Typography,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    TablePagination,
    // Stack
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
// import PreviewIcon from '@mui/icons-material/Preview';
import PndApi from 'src/utils/pnd/PndRestApi';
import { OPERATION_TYPE_FOR_GOODS_RECEIVED, OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED } from 'src/utils/pnd/PndConstants';
import { formatDate } from 'src/utils/DateUtils';
import Badge from "react-bootstrap/Badge";
import FreeRiderDetails from 'src/containers/PnD/Order/OrderCreation/View/components/FreeRiderComponents/FreeRiderDetails';

const AssignOrderGrid = ({ selectedHubId }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Assign Modal States
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, [selectedHubId]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await PndApi.GetAllOrders({
                orderType: 0,
                orderPriorityTypeId: 0,
                hubId: selectedHubId
            });
            if (response.status === 200 && response.data.status === 200) {
                // Filter for "Goods Received" status
                const allOrders = response.data.data || [];
                const goodsReceivedOrders = allOrders.filter(order => order.orderStatusId === OPERATION_TYPE_FOR_GOODS_RECEIVED);
                setOrders(goodsReceivedOrders);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error('Error fetching assignable orders:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignClick = (order) => {
        setSelectedOrder(order);
        setAssignModalOpen(true);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getOrderTypeBadge = (orderType) => {
        if (orderType === 1) return <Badge bg="success" style={{ fontSize: '10px' }}>DO</Badge>;
        if (orderType === 2) return <Badge bg="primary" style={{ fontSize: '10px' }}>PU</Badge>;
        return <Badge bg="warning" style={{ fontSize: '10px' }}>PnD</Badge>;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress sx={{ color: '#32CA73' }} />
            </Box>
        );
    }

    const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer component={Card} sx={{ boxShadow: 'none', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>ORDER NO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>ERP NO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>TYPE</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>FROM</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>TO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>SCHEDULED</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>STATUS</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '12px' }}>ACTIONS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedOrders.length > 0 ? paginatedOrders.map((order, index) => (
                            <TableRow key={order.orderId} sx={{ '&:hover': { backgroundColor: '#f1f5f9' } }}>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{page * rowsPerPage + index + 1}</TableCell>
                                <TableCell sx={{ fontSize: '11px', fontWeight: 'bold', py: 0.5 }}>{order.orderNumber}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{order.erpOrderNo || '—'}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>{getOrderTypeBadge(order.orderType)}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{order.fromName}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{order.toName}</TableCell>
                                <TableCell sx={{ fontSize: '11px', py: 0.5 }}>{formatDate(order.scheduledDateTimeSlot)}</TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                    <Chip label={order.orderStatus} size="small" color="success" variant="outlined" sx={{ fontSize: '10px', height: '20px' }} />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <Tooltip title="Assign Order">
                                        <IconButton size="small" color="primary" onClick={() => handleAssignClick(order)} sx={{ p: 0.5 }}>
                                            <AssignmentIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#999' }}>
                                    No orders found with status "Goods Received"
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={orders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {assignModalOpen && selectedOrder && (
                <FreeRiderDetails
                    modalShow={assignModalOpen}
                    setModalShow={setAssignModalOpen}
                    isOrderId={selectedOrder.orderId}
                    isOrderNo={selectedOrder.orderNumber}
                    isOrderType={selectedOrder.orderType}
                    isOperationType={OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED}
                    setRedirect={() => fetchOrders()} // Refresh after assign
                    setSelectedDriverId={setSelectedDriverId}
                    selectedDriverId={selectedDriverId}
                    selectedHubId={selectedHubId}
                />
            )}
        </Box>
    );
};

export default AssignOrderGrid;
